import { useState, useEffect, useMemo, useCallback } from "react";
import { userApi } from "@api/users/user";
import * as utils from "../utils";
import { CONFIG } from "../config";

// Fetch user profiles
export const useUserProfiles = (processedSessions) => {
  const [userProfiles, setUserProfiles] = useState({});
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  const fetchUserProfiles = useCallback(async (sessions) => {
    if (!sessions.length) return;
    setLoadingProfiles(true);

    const uniqueUserNames = [
      ...new Set(sessions.map((s) => s.userName).filter(Boolean)),
    ];
    const profiles = {},
      errors = {};

    await Promise.allSettled(
      uniqueUserNames.map(async (userName) => {
        try {
          const res = await userApi.getUserProfileByUsername(userName);
          profiles[userName] = res.data;
        } catch (err) {
          console.error(`Failed to fetch profile for ${userName}:`, err);
          errors[userName] = err.message;
        }
      })
    );

    setUserProfiles(profiles);
    setProfileErrors(errors);
    setLoadingProfiles(false);
  }, []);

  useEffect(() => {
    processedSessions.length && fetchUserProfiles(processedSessions);
  }, [processedSessions, fetchUserProfiles]);

  return { userProfiles, loadingProfiles, profileErrors, fetchUserProfiles };
};

// Process sessions
export const useProcessedSessions = (allSessions) =>
  useMemo(
    () =>
      allSessions
        .map((s) => ({
          id: s.id || `${s.libraryId}-${Date.now()}`,
          userName: s.userName || s.user_name || "Unknown User",
          libraryId: s.libraryId || s.library_id || "",
          program: s.program || "",
          year: s.year || "",
          email: s.email || "",
          purposeOfVisit:
            s.purposeOfVisit || s.purpose_of_visit || "Not specified",
          checkInTime: utils.formatDateTime(
            s.inTime || s.in_time || s.inTimeDisplay
          ),
          checkOutTime:
            s.outTime || s.out_time
              ? utils.formatDateTime(s.outTime || s.out_time)
              : "Still active",
          duration: utils.calculateDuration(
            s.inTime || s.in_time,
            s.outTime || s.out_time
          ),
          isActive: !s.outTime && !s.out_time,
          sessionDate: s.inTime || s.in_time || s.inTimeDisplay,
          rawSession: s,
        }))
        .sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate)),
    [allSessions]
  );

// Filters
export const useFilters = (processedSessions, userProfiles) => {
  const [filters, setFilters] = useState(CONFIG.INITIAL_FILTERS);

  const enhancedSessions = useMemo(() => {
    const sessionsByUser = processedSessions.reduce((acc, s) => {
      (acc[s.userName] = acc[s.userName] || []).push(s); // fixed for compatibility
      return acc;
    }, {});

    return processedSessions.map((s) => {
      const profile = userProfiles[s.userName] || {};
      return {
        ...s,
        sessionCount: sessionsByUser[s.userName]?.length || 1,
        userProfile: profile,
        fullName: profile.full_name || s.userName,
        department: profile.department || "",
        position: profile.position || "",
        program: profile.program || s.program || "",
        year: profile.year || s.year || "",
      };
    });
  }, [processedSessions, userProfiles]);

  const filterOptions = useMemo(
    () => ({
      programs: utils.createDropdownOptions(
        utils.getUniqueValues(enhancedSessions, "program")
      ),
      years: utils.createDropdownOptions(
        utils.getUniqueValues(enhancedSessions, "year")
      ),
      purposes: utils.createDropdownOptions(
        utils.getUniqueValues(enhancedSessions, "purposeOfVisit")
      ),
      status: CONFIG.STATUS_OPTIONS,
    }),
    [enhancedSessions]
  );

  const filteredSessions = useMemo(
    () =>
      enhancedSessions.filter((s) => {
        if (filters.program && s.program !== filters.program) return false;
        if (filters.year && s.year !== filters.year) return false;
        if (
          filters.purposeOfVisit &&
          s.purposeOfVisit !== filters.purposeOfVisit
        )
          return false;
        if (filters.status && s.isActive !== (filters.status === "active"))
          return false;

        if (filters.dateRange.start || filters.dateRange.end) {
          const date = new Date(s.sessionDate);
          if (
            filters.dateRange.start &&
            date < new Date(filters.dateRange.start)
          )
            return false;
          if (
            filters.dateRange.end &&
            date > new Date(filters.dateRange.end + "T23:59:59")
          )
            return false;
        }

        if (filters.searchTerm) {
          const search = filters.searchTerm.toLowerCase();
          return [
            s.userName,
            s.fullName,
            s.libraryId,
            s.program,
            s.year,
            s.purposeOfVisit,
            s.department,
            s.position,
          ].some((f) => f?.toLowerCase().includes(search));
        }
        return true;
      }),
    [enhancedSessions, filters]
  );

  return {
    filters,
    setFilters,
    filterOptions,
    filteredSessions,
    enhancedSessions,
  };
};
